import React from 'react';
import {
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UploadedContract, UploadedContractErrors, SalaryBasisOption, CalcTypeOption, JobPositionUploadOption } from '../../../hooks/useContracts';
import { isCalcTypeValid, isSalaryBasisValid, isJobPositionValid, toDateInputValue } from '../../../hooks/useContracts';
import type { Employee } from '../../../types/employee';

interface UploadContractPreviewMobileProps {
    contracts: UploadedContract[];
    employees: Employee[];
    salaryBasisOptions: SalaryBasisOption[];
    salaryBasisLoading: boolean;
    calcTypeOptions: CalcTypeOption[];
    calcTypeLoading: boolean;
    jobPositionOptions: JobPositionUploadOption[];
    jobPositionLoading: boolean;
    jobPositionLoaded?: boolean;
    onChange: (rowId: number, field: keyof UploadedContract, value: any) => void;
    errors?: UploadedContractErrors;
    showAllErrors?: boolean;
}

const UploadContractPreviewMobile: React.FC<UploadContractPreviewMobileProps> = ({
    contracts,
    employees,
    salaryBasisOptions,
    salaryBasisLoading,
    calcTypeOptions,
    calcTypeLoading,
    jobPositionOptions,
    jobPositionLoading,
    jobPositionLoaded = false,
    onChange,
    errors = {},
    showAllErrors = false,
}) => {
    const { t } = useTranslation();
    if (contracts.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('employee.contract.upload.noContractsToPreview')}
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            {contracts.map((contract) => {
                const resolvedEmp = employees.find((e) => String(e.id) === contract.resolvedEmployeeId);
                const calcValid = isCalcTypeValid(contract.payrollCalcType, calcTypeOptions);
                const salaryValid = isSalaryBasisValid(contract.salaryBasis, salaryBasisOptions);
                const jobValid = isJobPositionValid(contract.jobPosition, jobPositionOptions, jobPositionLoading);

                return (
                    <Card key={contract._rowId} variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                {resolvedEmp
                                    ? `${resolvedEmp.firstName} ${resolvedEmp.lastName}`
                                    : t('employee.contract.upload.personalNoLabel', {personalNo: contract.employeePersonalNo})}
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack spacing={1.5}>

                                {/* Employee select */}
                                <FormControl fullWidth size="small" error={showAllErrors && !!errors.employeePersonalNo}>
                                    <Select
                                        value={contract.resolvedEmployeeId}
                                        displayEmpty
                                        onChange={(e) => onChange(contract._rowId, 'resolvedEmployeeId', e.target.value)}
                                        renderValue={(val) =>
                                            val
                                                ? (() => {
                                                    const e = employees.find((em) => String(em.id) === val);
                                                    return e ? `${e.firstName} ${e.lastName}` : String(val);
                                                })()
                                                : <em style={{ color: '#aaa' }}>{t('employee.contract.upload.selectEmployee')}</em>
                                        }
                                    >
                                        {employees.map((em) => (
                                            <MenuItem key={String(em.id)} value={String(em.id)}>
                                                {em.firstName} {em.lastName} ({em.personalNo})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {!resolvedEmp && contract.employeePersonalNo && (
                                        <FormHelperText>
                                            {(showAllErrors && errors.employeePersonalNo)
                                                ? errors.employeePersonalNo
                                                : t('employee.contract.upload.personalNoNotFound', {personalNo: contract.employeePersonalNo})}
                                        </FormHelperText>
                                    )}
                                    {showAllErrors && errors.employeePersonalNo && resolvedEmp && (
                                        <FormHelperText>{errors.employeePersonalNo}</FormHelperText>
                                    )}
                                </FormControl>

                                <TextField
                                                    label={t('employee.contract.field.startDate')}
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={toDateInputValue(contract.startDate)}
                                    onChange={(e) => onChange(contract._rowId, 'startDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    error={showAllErrors && !!errors.startDate}
                                    helperText={showAllErrors ? errors.startDate : undefined}
                                />

                                <TextField
                                    label={t('employee.contract.field.endDate')}
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={toDateInputValue(contract.endDate)}
                                    onChange={(e) => onChange(contract._rowId, 'endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    error={showAllErrors && !!errors.endDate}
                                    helperText={showAllErrors ? errors.endDate : undefined}
                                />
                                {/* Job Position */}
                                <FormControl fullWidth size="small" error={(!isJobPositionValid(contract.jobPosition, jobPositionOptions, jobPositionLoading) && jobPositionLoaded) || (showAllErrors && !!errors.jobPosition)}>
                                    <Select
                                        value={jobValid ? contract.jobPosition : ''}
                                        displayEmpty
                                        disabled={!resolvedEmp}
                                        onChange={(e) => onChange(contract._rowId, 'jobPosition', e.target.value)}
                                        startAdornment={
                                            jobPositionLoading ? (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={14} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        renderValue={(val) =>
                                            val
                                                ? (jobPositionOptions.find((o) => o.id === val)?.name ?? String(val))
                                                : <em style={{ color: '#aaa' }}>{resolvedEmp ? t('employee.contract.field.jobPosition') : t('employee.contract.upload.selectEmployeeFirst')}</em>
                                        }
                                    >
                                        {jobPositionOptions.map((o) => (
                                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {!jobValid && jobPositionLoaded && resolvedEmp && (
                                        <FormHelperText>
                                            {jobPositionOptions.length === 0
                                                ? t('employee.contract.upload.noJobPositionsForBranch')
                                                : t('employee.contract.upload.jobPositionNotFound', {jobPosition: contract.jobPosition})}
                                        </FormHelperText>
                                    )}
                                    {jobValid && showAllErrors && errors.jobPosition && (
                                        <FormHelperText>{errors.jobPosition}</FormHelperText>
                                    )}
                                </FormControl>


                                {/* Payroll Calc Type */}
                                <FormControl fullWidth size="small" error={showAllErrors && !!errors.payrollCalcType}>
                                    <Select
                                        value={calcValid ? contract.payrollCalcType : ''}
                                        displayEmpty
                                        onChange={(e) => onChange(contract._rowId, 'payrollCalcType', e.target.value)}
                                        startAdornment={
                                            calcTypeLoading ? (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={14} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        renderValue={(val) =>
                                            val
                                                ? (calcTypeOptions.find((o) => o.id === val)?.name ?? String(val))
                                                : <em style={{ color: '#aaa' }}>{t('employee.contract.field.payrollCalcType')}</em>
                                        }
                                    >
                                        {calcTypeOptions.map((o) => (
                                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {showAllErrors && errors.payrollCalcType && (
                                        <FormHelperText>{errors.payrollCalcType}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* With Contribution */}
                                <FormControl fullWidth size="small" error={showAllErrors && !!errors.withContribution}>
                                    <Select
                                        value={contract.withContribution}
                                        displayEmpty
                                        onChange={(e) => onChange(contract._rowId, 'withContribution', e.target.value)}
                                        renderValue={(val) =>
                                            val === '1' ? t('common.actions.yes') : val === '0' ? t('common.actions.no') : <em style={{ color: '#aaa' }}>{t('employee.contract.field.withContribution')}</em>
                                        }
                                    >
                                        <MenuItem value="1">{t('common.actions.yes')}</MenuItem>
                                        <MenuItem value="0">{t('common.actions.no')}</MenuItem>
                                    </Select>
                                    {showAllErrors && errors.withContribution && (
                                        <FormHelperText>{errors.withContribution}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Salary Basis */}
                                <FormControl fullWidth size="small" error={showAllErrors && !!errors.salaryBasis}>
                                    <Select
                                        value={salaryValid ? contract.salaryBasis : ''}
                                        displayEmpty
                                        onChange={(e) => onChange(contract._rowId, 'salaryBasis', e.target.value)}
                                        startAdornment={
                                            salaryBasisLoading ? (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={14} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        renderValue={(val) =>
                                            val
                                                ? (salaryBasisOptions.find((o) => o.id === val)?.name ?? String(val))
                                                : <em style={{ color: '#aaa' }}>{t('employee.contract.field.salaryBasis')}</em>
                                        }
                                    >
                                        {salaryBasisOptions.map((o) => (
                                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {showAllErrors && errors.salaryBasis && (
                                        <FormHelperText>{errors.salaryBasis}</FormHelperText>
                                    )}
                                </FormControl>

                                <TextField
                                    label={t('employee.contract.field.baseAmount')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={contract.baseAmount}
                                    onChange={(e) => onChange(contract._rowId, 'baseAmount', e.target.value)}
                                    error={showAllErrors && !!errors.baseAmount}
                                    helperText={showAllErrors ? errors.baseAmount : undefined}
                                />

                                <TextField
                                    label={t('employee.contract.field.workload')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={contract.workload}
                                    onChange={(e) => onChange(contract._rowId, 'workload', e.target.value)}
                                    error={showAllErrors && !!errors.workload}
                                    helperText={showAllErrors ? errors.workload : undefined}
                                />

                                <TextField
                                    label={t('employee.contract.field.employerInsuranceBenefit')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={contract.employerInsuranceBenefit}
                                    onChange={(e) => onChange(contract._rowId, 'employerInsuranceBenefit', e.target.value)}
                                    error={showAllErrors && !!errors.employerInsuranceBenefit}
                                    helperText={showAllErrors ? errors.employerInsuranceBenefit : undefined}
                                />

                                <TextField
                                    label={t('employee.contract.field.familyInsuranceDeduction')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={contract.familyInsuranceDeduction}
                                    onChange={(e) => onChange(contract._rowId, 'familyInsuranceDeduction', e.target.value)}
                                    error={showAllErrors && !!errors.familyInsuranceDeduction}
                                    helperText={showAllErrors ? errors.familyInsuranceDeduction : undefined}
                                />

                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default UploadContractPreviewMobile;
